import { useCallback, useMemo, useReducer } from "react";

import { useDraftCommitScheduler } from "~/v3-poc/features/workspace/shared/layoutSettings/draftCommit";
import type {
  LayoutSettingChange,
  LayoutSettingSectionView,
} from "~/v3-poc/features/workspace/shared/layoutSettings/layoutSettings.types";
import type { RglSettingId } from "./reactGridLayoutSettings.schema";
import type { ReactGridLayoutSettings } from "./reactGridLayoutSettings.types";
import {
  createInitialRglSettingsState,
  rglSettingsReducer,
} from "./rglSettings.reducer";
import { buildRglSettingsSections } from "./rglSettings.sections";

export interface ReactGridLayoutSettingsControllerValue {
  config: ReactGridLayoutSettings;
  sections: ReadonlyArray<LayoutSettingSectionView>;
  expanded: boolean;
  panelId: "workspace-rgl-settings-panel";
  buttonId: "workspace-rgl-settings-button";
  onToggleExpanded: () => void;
  onFieldChange: (fieldId: RglSettingId, change: LayoutSettingChange) => void;
  hydrate: (config: ReactGridLayoutSettings) => void;
  reset: () => void;
}

export interface UseReactGridLayoutSettingsControllerOptions {
  /** Hydrated settings from localStorage; omitted → defaults. */
  initialConfig?: ReactGridLayoutSettings;
}

const PANEL_ID = "workspace-rgl-settings-panel" as const;
const BUTTON_ID = "workspace-rgl-settings-button" as const;

export const useReactGridLayoutSettingsController = (
  options: UseReactGridLayoutSettingsControllerOptions = {},
): ReactGridLayoutSettingsControllerValue => {
  const [state, dispatch] = useReducer(
    rglSettingsReducer,
    options.initialConfig,
    createInitialRglSettingsState,
  );

  const scheduler = useDraftCommitScheduler<RglSettingId>(
    useCallback((fieldId) => {
      dispatch({ type: "commit-draft", fieldId, normalize: false });
    }, []),
  );

  const onToggleExpanded = useCallback(() => {
    dispatch({ type: "toggle-expanded" });
  }, []);

  const reset = useCallback(() => {
    // Drop the pending draft first: a timer surviving Restart would apply
    // pre-restart settings on top of the freshly reset config.
    scheduler.cancel();
    dispatch({ type: "reset" });
  }, [scheduler]);

  const hydrate = useCallback(
    (config: ReactGridLayoutSettings) => {
      scheduler.cancel();
      dispatch({ type: "hydrate", config });
    },
    [scheduler],
  );

  const onFieldChange = useCallback(
    (fieldId: RglSettingId, change: LayoutSettingChange) => {
      if (change.kind === "draft-commit") {
        scheduler.cancel(fieldId);
        dispatch({ type: "commit-draft", fieldId, normalize: true });
        return;
      }
      dispatch({ type: "field-change", fieldId, change });
      if (change.kind === "draft") {
        scheduler.schedule(fieldId);
      }
    },
    [scheduler],
  );

  const sections = useMemo(
    () => buildRglSettingsSections(state.config, state.drafts, state.errors),
    [state.config, state.drafts, state.errors],
  );

  return {
    config: state.config,
    sections,
    expanded: state.expanded,
    panelId: PANEL_ID,
    buttonId: BUTTON_ID,
    onToggleExpanded,
    onFieldChange,
    hydrate,
    reset,
  };
};
